/**
 * Import de `.dvw` — la parte PURA de la pantalla (ítem 2.7 del plan).
 *
 * Acá vive todo lo que se puede testear sin DOM ni red: leer los bytes a
 * base64, partir la temporada en tandas del tamaño que acepta el API, juntar
 * las respuestas de varias tandas en un solo resultado y traducir los estados.
 * `pages/ImportPage.tsx` es sólo la pantalla.
 *
 * POR QUÉ TANDAS
 * --------------
 * El API acepta hasta 25 archivos por request (`MAX_FILES_PER_REQUEST`) y tiene
 * tope de cuerpo. Una temporada de una liga nacional son ~130 partidos, así que
 * la pantalla parte sola y va mostrando el avance: el operador elige la carpeta
 * entera una vez y no tiene que saber que abajo hubo seis requests. Como el
 * import es idempotente por contenido, una tanda que falla se reintenta sin
 * consecuencias.
 */
import type {
  DvwFileResult,
  DvwImportFile,
  DvwImportResponse,
  DvwImportSummary,
} from './api-client';

/**
 * Archivos por request. Es el mismo número que el API declara en
 * `MAX_FILES_PER_REQUEST`; si allá subiera, acá sólo se harían tandas más
 * chicas de lo necesario (nunca al revés, que sería un 400).
 */
export const FILES_PER_BATCH = 25;

/** Bytes → base64 sin `Buffer` (esto corre en el browser). */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  // De a pedazos: `String.fromCharCode(...bytes)` con un archivo de 300 kB
  // revienta el stack del browser (demasiados argumentos).
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  const g = globalThis as { btoa?: (data: string) => string };
  if (g.btoa) return g.btoa(binary);
  // Node (tests): `btoa` existe desde la 16, pero no se asume.
  return Buffer.from(binary, 'binary').toString('base64');
}

/** Parte la lista en tandas de `size` (la última puede ser más chica). */
export function batches<T>(items: readonly T[], size = FILES_PER_BATCH): T[][] {
  const safe = Math.max(1, Math.trunc(size));
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += safe) out.push(items.slice(i, i + safe));
  return out;
}

/** ¿El nombre tiene pinta de `.dvw`? Sólo para avisar, no para bloquear. */
export function looksLikeDvw(name: string): boolean {
  return /\.dvw$/i.test(name.trim());
}

export const EMPTY_SUMMARY: DvwImportSummary = {
  imported: 0,
  alreadyImported: 0,
  preview: 0,
  failed: 0,
  events: 0,
  teamsCreated: 0,
  teamsMerged: 0,
  unmapped: 0,
};

/**
 * Junta las respuestas de las tandas en un resultado único.
 *
 * Los archivos se concatenan en orden y los contadores se suman; `teamsWritable`
 * es el AND de las tandas —si en alguna no se pudo escribir la base de equipos,
 * la pantalla no puede decir que sí— y `dryRun` sale de la primera (todas las
 * tandas de una corrida son del mismo tipo).
 */
export function mergeResponses(responses: readonly DvwImportResponse[]): DvwImportResponse {
  const files: DvwFileResult[] = [];
  const summary: DvwImportSummary = { ...EMPTY_SUMMARY };
  let teamsWritable = true;

  for (const response of responses) {
    files.push(...response.files);
    summary.imported += response.summary.imported;
    summary.alreadyImported += response.summary.alreadyImported;
    summary.preview += response.summary.preview;
    summary.failed += response.summary.failed;
    summary.events += response.summary.events;
    summary.teamsCreated += response.summary.teamsCreated;
    summary.teamsMerged += response.summary.teamsMerged;
    summary.unmapped += response.summary.unmapped;
    if (!response.teamsWritable) teamsWritable = false;
  }

  return {
    dryRun: responses[0]?.dryRun ?? true,
    count: files.length,
    teamsWritable,
    summary,
    files,
  };
}

/**
 * Equipos únicos de un preview, con lo que le va a pasar a cada uno.
 *
 * Un club aparece en todas sus fechas: mostrar "Bolívar" catorce veces no dice
 * nada. Se muestra una vez, con la cuenta de archivos que lo traen y el estado
 * más fuerte (crear gana sobre mezclar, y mezclar sobre no tocar), que es lo
 * que el operador quiere saber antes de apretar Importar.
 */
export interface TeamRollup {
  teamId: string;
  name: string;
  files: number;
  state: 'created' | 'merged' | 'unchanged' | 'skipped';
  addedPlayers: number;
}

const STATE_RANK: Record<TeamRollup['state'], number> = {
  created: 3,
  merged: 2,
  unchanged: 1,
  skipped: 0,
};

export function rollupTeams(files: readonly DvwFileResult[]): TeamRollup[] {
  const byId = new Map<string, TeamRollup>();
  for (const file of files) {
    for (const team of file.teams) {
      const current = byId.get(team.teamId);
      if (!current) {
        byId.set(team.teamId, {
          teamId: team.teamId,
          name: team.name,
          files: 1,
          state: team.state,
          addedPlayers: team.addedPlayers.length,
        });
        continue;
      }
      current.files += 1;
      current.addedPlayers += team.addedPlayers.length;
      if (STATE_RANK[team.state] > STATE_RANK[current.state]) current.state = team.state;
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Lo que NO se pudo mapear, sumado en toda la tanda y ordenado igual que en el
 * API: primero lo desconocido, que es lo único que amerita abrir el archivo.
 */
export interface UnmappedRollup {
  code: string;
  sample: string;
  kind: 'green_code' | 'setter_change' | 'sanction_no_team' | 'unknown_code';
  count: number;
  files: number;
}

const KIND_RANK: Record<UnmappedRollup['kind'], number> = {
  unknown_code: 0,
  green_code: 1,
  setter_change: 2,
  sanction_no_team: 3,
};

export function rollupUnmapped(files: readonly DvwFileResult[]): UnmappedRollup[] {
  const byCode = new Map<string, UnmappedRollup>();
  for (const file of files) {
    for (const group of file.report?.unmapped ?? []) {
      const current = byCode.get(group.code);
      if (!current) {
        byCode.set(group.code, {
          code: group.code,
          sample: group.sample,
          kind: group.kind,
          count: group.count,
          files: 1,
        });
        continue;
      }
      current.count += group.count;
      current.files += 1;
    }
  }
  return [...byCode.values()].sort(
    (a, b) => KIND_RANK[a.kind] - KIND_RANK[b.kind] || b.count - a.count || a.code.localeCompare(b.code),
  );
}

/** Total de eventos que el preview dice que van a entrar. */
export function totalEvents(files: readonly DvwFileResult[]): number {
  return files.reduce((total, file) => total + (file.report?.events ?? 0), 0);
}

/** Archivos que van a entrar de verdad (los que ya estaban no cuentan). */
export function importableFiles(files: readonly DvwFileResult[]): number {
  return files.filter((file) => file.outcome === 'preview').length;
}

/** Nombre → payload del API. Existe para que el orden de campos sea uno solo. */
export function toImportFile(name: string, bytes: Uint8Array): DvwImportFile {
  return { name, content: bytesToBase64(bytes), encoding: 'base64' };
}
